import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { MIN_POINT_FOR_LIVE_VIEWING } from "~/constants/actionsPoint";
import {
  getPointQueryOptions,
  liveChannelsQueryOptions,
} from "~/features/live/api";
import {
  publicImagesQueryOptions,
  userProfileQueryOptions,
} from "~/features/profile/api";
import { UserProfileSkeleton } from "~/features/profile/components/UserProfileSkeleton";
import { UserProfileTemplate } from "~/features/profile/UserProfileTemplate";
import { useInsufficientPointSheetStore } from "~/stores/useInsufficientPointSheetStore";

const LiveBroadcasterProfilePage = () => {
  const { userId } = Route.useParams();
  const { liveChannels } = Route.useLoaderData();
  const navigate = useNavigate();
  const { data: point } = useQuery(getPointQueryOptions());

  const channel = [
    ...liveChannels.in_live_list,
    ...liveChannels.standby_list,
  ].find((c) => c.broadcaster_id === userId);

  const isPointShortage =
    point !== undefined && point.point < MIN_POINT_FOR_LIVE_VIEWING;
  const canWatchLive = !isPointShortage;

  /** チャンネル情報をセッションに保存してライブ画面へ遷移する */
  function enterLiveNow() {
    if (!channel) return;
    sessionStorage.setItem("live_channel", JSON.stringify(channel));
    navigate({ to: "/call/live" });
  }

  function handleWatchLive() {
    if (!channel) return;
    if (isPointShortage) {
      // ポイント不足: プロフィール上でチャージシートを表示する（閉じても遷移しない）。
      useInsufficientPointSheetStore.getState().show({
        needed: {
          labelKey: "insufficientPointSheet.neededLabelLive",
          value: channel.point_per_minute ?? MIN_POINT_FOR_LIVE_VIEWING,
          unitKey: "insufficientPointSheet.unitPerMin",
        },
        onClose: () => {},
        onChargeSuccess: () => {
          // チャージ成功後にライブへ入室
          enterLiveNow();
        },
      });
      return;
    }
    enterLiveNow();
  }

  return (
    <UserProfileTemplate
      userId={userId}
      variant={channel ? "liveBroadcaster" : "page"}
      onWatchLive={handleWatchLive}
      canWatchLive={canWatchLive}
      live={
        channel
          ? {
              recordingId: channel.recording_id,
              thumbnailUrl: channel.thumbnail_url ?? channel.broadcaster_avatar,
              viewerCount: channel.viewer_count,
              isStandby: channel.is_standby,
            }
          : undefined
      }
    />
  );
};

export const Route = createFileRoute(
  "/_authenticated/profile/live-broadcaster/$userId",
)({
  loader: async ({ context: { queryClient }, params: { userId } }) => {
    const [, , liveChannels] = await Promise.all([
      queryClient.ensureQueryData(userProfileQueryOptions(userId)),
      queryClient.ensureQueryData(publicImagesQueryOptions(userId)),
      queryClient.ensureQueryData(liveChannelsQueryOptions()),
      queryClient.ensureQueryData(getPointQueryOptions()),
    ]);
    return { liveChannels };
  },
  pendingMs: 200,
  pendingMinMs: 300,
  pendingComponent: UserProfileSkeleton,
  component: LiveBroadcasterProfilePage,
});
